import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useTeams } from './TeamsContext'
import { api, AppConfigResponse } from '../lib/api'

interface ConfigContextType {
  config: AppConfigResponse | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const ConfigContext = createContext<ConfigContextType>({
  config: null,
  isLoading: true,
  error: null,
  refetch: async () => {},
})

export function useConfig() {
  return useContext(ConfigContext)
}

interface ConfigProviderProps {
  children: ReactNode
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<AppConfigResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isInitialized, hasSsoToken, error: teamsError } = useTeams()

  const fetchConfig = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await api.getConfig()
      setConfig(data)
    } catch (err) {
      console.error('Config loading error:', err)
      const message = err instanceof Error ? err.message : String(err)
      setError(message || 'Failed to load configuration')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    // If Teams host initialization failed, don't call protected APIs.
    if (teamsError && !hasSsoToken) {
      setIsLoading(false)
      setError(teamsError)
      return
    }

    fetchConfig()
  }, [isInitialized, hasSsoToken, teamsError])

  return <ConfigContext.Provider value={{ config, isLoading, error, refetch: fetchConfig }}>{children}</ConfigContext.Provider>
}
